import React from "react";
import type { ColumnsType } from "antd/es/table";
import type { SortOrder } from "antd/es/table/interface";
import type { TFunction } from "i18next";

import { OrderByColumnInViolation } from "~/schemas/generated/graphql";
import {
  AnsweredCell,
  AnswerStatusCell,
  CategoryCell,
  DateCell,
  DealerNameCell,
  DefaultTextCell,
  LocalizedTextCell,
  RationaleCell,
  ViolationTypeCell,
} from "~/shared/ui/TableV2/ui/Cells";

/** Колонки таблицы, по которым возможна серверная сортировка, и их API-ключи */
export const VIOLATION_SORTABLE_API_KEYS: Record<
  string,
  OrderByColumnInViolation
> = {
  answered: OrderByColumnInViolation.Answer,
  federalArea: OrderByColumnInViolation.Of,
  dealerName: OrderByColumnInViolation.DealerName,
  deadline: OrderByColumnInViolation.Deadline,
};

export const getViolationsColumns = (
  t: TFunction,
  options: string[],
  myRole: string,
  sortField?: string,
  sortType?: string
): ColumnsType<any> =>
  options.map((option) => {
    const translationKey = `violationsTable.${option}`;
    const translatedTitle = t(translationKey);
    const title = translatedTitle !== translationKey ? translatedTitle : option;
    const lang = localStorage.getItem("language");

    const sortableApiKey = VIOLATION_SORTABLE_API_KEYS[option];
    const sortOrder: SortOrder | undefined =
      sortableApiKey &&
      sortField === sortableApiKey &&
      (sortType === "ASC" || sortType === "DESC")
        ? sortType === "DESC"
          ? "descend"
          : "ascend"
        : undefined;

    const baseColumn = {
      title,
      dataIndex: option,
      // Для сортируемых колонок key = API-ключ: TableV2 отдаёт его
      // в onSortChange как sortField
      key: sortableApiKey ?? option,
      ...(sortableApiKey ? { sorter: true, sortOrder } : {}),
    };

    switch (option) {
      case "rationale":
        return {
          ...baseColumn,
          render: (rationale: any) => <RationaleCell rationale={rationale} />,
        };

      case "answered":
        return {
          ...baseColumn,
          render: (_: any, record: any) => {
            const comment =
              record.comments && record.comments.length > 0
                ? record.comments[0]
                : "-";
            return <AnsweredCell comment={comment} myRole={myRole} />;
          },
        };

      case "answerStatus":
        return {
          ...baseColumn,
          render: (status: any, record: any) => (
            <AnswerStatusCell
              status={status}
              hasPunishment={record.punishment}
              t={t}
            />
          ),
        };

      case "federalArea":
        return {
          ...baseColumn,
          render: (federalArea: any) => (
            <LocalizedTextCell localizedText={federalArea} lang={lang} />
          ),
        };

      case "dealerName":
        return {
          ...baseColumn,
          render: (dealerName: any) => (
            <DealerNameCell dealerName={dealerName} lang={lang} />
          ),
        };

      case "deadline":
      case "DocDate":
        return {
          ...baseColumn,
          render: (date: any) => <DateCell date={date} />,
        };

      case "violationType":
        return {
          ...baseColumn,
          render: (violationType: any) => (
            <ViolationTypeCell violationType={violationType} lang={lang} />
          ),
        };

      case "category":
        return {
          ...baseColumn,
          render: (category: any) => <CategoryCell category={category} t={t} />,
        };

      case "subdivision":
        return {
          ...baseColumn,
          width: 200,
          render: (subdivision: any) => (
            <DefaultTextCell text={subdivision} asDiv />
          ),
        };

      case "DocNum":
      case "reportedByUser":
        return {
          ...baseColumn,
          render: (text: any) => <DefaultTextCell text={text} asDiv />,
        };

      default:
        return baseColumn;
    }
  });
